(() => {
  'use strict';
  const configNode = document.getElementById('yofeely-barcode-search');
  if (!configNode) return;
  const config = JSON.parse(configNode.textContent);
  const cacheKey = 'yofeely:barcodes:v1:' + config.root;
  const maxAge = 5 * 60 * 1000;
  let catalogPromise;
  const isSuffix = value => /^\d{4}$/.test(value.trim());
  const normalize = value => String(value || '').replace(/[\s-]/g, '');
  function buildIndex(items) {
    const index = {};
    for (const [id, barcodes] of items) {
      for (const value of barcodes) {
        const barcode = normalize(value);
        if (!/^\d{4,}$/.test(barcode)) continue;
        const suffix = barcode.slice(-4);
        const ids = index[suffix] || (index[suffix] = []);
        if (!ids.includes(String(id))) ids.push(String(id));
      }
    }
    return index;
  }
  async function catalogPage(page) {
    const url = new URL(config.root + 'collections/all', location.origin);
    url.searchParams.set('view', 'yofeely-barcodes');
    url.searchParams.set('sort_by', 'created-ascending');
    url.searchParams.set('page', page);
    const response = await fetch(url, {signal: AbortSignal.timeout(20000)});
    if (!response.ok) throw new Error('Barcode catalog unavailable');
    const data = await response.json();
    if (!Array.isArray(data.items) || !Number.isInteger(data.pages)) throw new Error('Invalid barcode catalog');
    return data;
  }
  function loadIndex() {
    if (catalogPromise) return catalogPromise;
    catalogPromise = (async () => {
      try {
        const cached = JSON.parse(sessionStorage.getItem(cacheKey));
        if (cached && Date.now() - cached.time < maxAge && cached.index) return cached.index;
      } catch {}
      const first = await catalogPage(1);
      const rows = first.items.slice();
      let page = 2;
      await Promise.all(Array.from({length: Math.min(4, first.pages - 1)}, async () => {
        while (page <= first.pages) {
          const next = page++;
          const data = await catalogPage(next);
          if (data.count !== first.count) throw new Error('Catalog changed; retry search');
          rows.push(...data.items);
        }
      }));
      if (rows.length !== first.count) throw new Error('Incomplete barcode catalog');
      const index = buildIndex(rows);
      try { sessionStorage.setItem(cacheKey, JSON.stringify({time: Date.now(), index})); } catch {}
      return index;
    })().catch(error => { catalogPromise = null; throw error; });
    return catalogPromise;
  }
  async function resolveQuery(term) {
    const clean = term.trim();
    if (!isSuffix(clean)) return clean;
    const index = await loadIndex();
    const ids = index[clean] || [];
    return [clean, ...ids.map(id => 'id:' + id)].join(' OR ');
  }
  function resultURL(query) {
    const url = new URL(config.search, location.origin);
    url.searchParams.set('q', query);
    url.searchParams.set('type', 'product');
    return url;
  }
  function cleanLabels(root, query, term) {
    if (query === term) return;
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    while (walker.nextNode()) {
      const node = walker.currentNode;
      if (!node.parentElement?.closest('script,style') && node.textContent.includes(query)) {
        node.textContent = node.textContent.replaceAll(query, term);
      }
    }
    root.querySelectorAll('input[name="q"]').forEach(input => {
      if (input.type !== 'hidden' && input.value === query) input.value = term;
    });
    root.querySelectorAll('[aria-label]').forEach(element => {
      element.setAttribute('aria-label', element.getAttribute('aria-label').replaceAll(query, term));
    });
  }
  function errorBox(container, retry) {
    container.replaceChildren();
    const box = document.createElement('div');
    box.className = 'hdt-text-center';
    box.style.padding = '2rem';
    box.setAttribute('role', 'alert');
    const text = document.createElement('p');
    text.textContent = config.error;
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'hdt-btn hdt-btn-outline';
    button.textContent = config.retry;
    button.addEventListener('click', retry);
    box.append(text, button);
    container.append(box);
  }
  customElements.whenDefined('hdt-predictive-search').then(() => {
    const proto = customElements.get('hdt-predictive-search').prototype;
    proto.getSearchResults = async function(term) {
      this.abortController2?.abort();
      const controller = new AbortController();
      this.abortController2 = controller;
      this.setLiveRegionLoadingState();
      const current = () => !controller.signal.aborted && this.getQuery() === term;
      try {
        const query = await resolveQuery(term);
        if (!current()) return;
        const numeric = isSuffix(term);
        const standard = numeric || document.documentElement.lang.toLowerCase().startsWith('he');
        const endpoint = standard ? config.search : themeHDN.routes.predictive_search_url;
        const url = new URL(endpoint, location.origin);
        const params = new URLSearchParams(standard ? 'type=product,page,article' : this.params);
        params.set('q', query);
        params.set('section_id', this.sectionIdResults);
        url.search = params.toString();
        const key = url.href;
        let markup = this.cachedResults[key];
        if (!markup) {
          const response = await fetch(url, {signal: controller.signal});
          if (!response.ok) throw new Error('Search unavailable');
          const html = new DOMParser().parseFromString(await response.text(), 'text/html');
          const section = html.getElementById('shopify-section-' + this.sectionIdResults);
          if (!section) throw new Error('Search section missing');
          cleanLabels(section, query, term);
          markup = section.innerHTML;
          this.cachedResults[key] = markup;
        }
        if (!current()) return;
        this.renderSearchResults(markup);
        if (numeric && this.btn_viewall) this.btn_viewall.href = resultURL(query).href;
      } catch (error) {
        if (!current()) return;
        this.removeAttribute('loading');
        this.skeleton?.classList.add('hdt-hidden');
        errorBox(this.predictiveSearchResults, () => this.getSearchResults(this.getQuery()));
        this.openSearch();
      }
    };
  });
  document.addEventListener('input', event => {
    const input = event.target;
    if (!input.matches?.('input[name="q"]')) return;
    const search = input.closest('hdt-predictive-search');
    search?.abortController2?.abort();
    if (/^\d{1,4}$/.test(input.value.trim())) loadIndex().catch(() => {});
  }, true);
  document.addEventListener('submit', async event => {
    const form = event.target;
    const input = form.querySelector?.('input[name="q"]');
    if (!input || !isSuffix(input.value) || new URL(form.action, location.origin).pathname !== config.search) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (form.dataset.barcodeBusy) return;
    const term = input.value.trim();
    form.dataset.barcodeBusy = 'true';
    form.setAttribute('aria-busy', 'true');
    try {
      const query = await resolveQuery(term);
      if (input.value.trim() !== term) return;
      const url = resultURL(query);
      url.searchParams.set('barcode_checked', '1');
      location.assign(url);
    } catch {
      let box = form.parentElement.querySelector('[data-barcode-error]');
      if (!box) {
        box = document.createElement('div');
        box.dataset.barcodeError = '';
        form.after(box);
      }
      errorBox(box, () => { box.remove(); form.requestSubmit(); });
    } finally {
      delete form.dataset.barcodeBusy;
      form.removeAttribute('aria-busy');
    }
  }, true);
  const url = new URL(location.href);
  const query = url.searchParams.get('q') || '';
  const expanded = /^(\d{4})(?: OR id:\d+)+$/.exec(query);
  if (expanded) {
    cleanLabels(document.body, query, expanded[1]);
    document.title = document.title.replaceAll(query, expanded[1]);
  }
  if (url.pathname === config.search && isSuffix(query) && !url.searchParams.has('barcode_checked') && !url.searchParams.has('view')) {
    const main = document.getElementById('MainContent');
    const status = document.createElement('div');
    status.style.cssText = 'padding:4rem;text-align:center';
    status.setAttribute('role', 'status');
    status.textContent = config.loading;
    main?.before(status);
    if (main) main.hidden = true;
    const run = async () => {
      try {
        const resolved = await resolveQuery(query);
        if (resolved !== query) {
          url.searchParams.set('q', resolved);
          url.searchParams.set('type', 'product');
          url.searchParams.set('barcode_checked', '1');
          location.replace(url);
        } else {
          status.remove();
          if (main) main.hidden = false;
        }
      } catch { errorBox(status, run); }
    };
    run();
  }
})();
